import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ThemeService } from '../../../core/services/theme.service';
import { EtablissementResponseDTO } from '../../../models/EtablissementResponseDTO';
import { environment } from '../../../../environments/environement';
import { SuperAdminService } from '../../../pages/super-admin/services/super-admin.service';

@Component({
  selector: 'app-index-page',
  templateUrl: './index-page.component.html',
  styleUrl: './index-page.component.css'
})
export class IndexPageComponent implements OnInit, OnDestroy {

  private mobileMenuOpen = false;
  private carouselIndex = 0;
  private carouselMax = 0;

  // ✅ Liste des établissements
  establishments: EtablissementResponseDTO[] = [];
  defaultLogo = 'assets/img/monkey.png';

  // ── Contact Form ───────────────────────────────────────
  contactData = { nom: '', email: '', sujet: '', message: '' };
  contactSending = false;
  contactSuccess = '';
  contactError = '';

  constructor(
    private translate: TranslateService,
    private themeService: ThemeService,
    private superAdminSvc: SuperAdminService
  ) {
    translate.addLangs(['fr', 'en', 'ar']);
    translate.setDefaultLang('fr');

    const saved = localStorage.getItem('selectedLanguage');
    const browserLang = translate.getBrowserLang();
    const lang = saved || (browserLang?.match(/fr|en|ar/) ? browserLang : 'fr');
    translate.use(lang!);
  }

  ngOnInit(): void {
    this.loadEstablishments();
    this.initCarousel();
    this.initNavbarScroll();
  }

  /** ✅ Charger tous les établissements */
  loadEstablishments(): void {
    this.themeService.getAllEstablishments().subscribe({
      next: (data) => {
        this.establishments = data;
        setTimeout(() => this.updateCarouselMax(), 500);
      },
      error: () => {}
    });
  }

  /** ✅ Construire l'URL du logo */
  getLogoUrl(logoPath: string | null | undefined): string {
    return this.themeService.buildLogoUrl(logoPath);
  }

  /** Submit contact form */
  submitContact(): void {
    if (!this.contactData.nom || !this.contactData.email || !this.contactData.sujet || !this.contactData.message) {
      this.contactError = 'Veuillez remplir tous les champs obligatoires.';
      return;
    }
    this.contactSending = true;
    this.contactError = '';
    this.contactSuccess = '';
    this.superAdminSvc.sendContactMessage(this.contactData).subscribe({
      next: () => {
        this.contactSuccess = 'Votre message a ete envoye avec succes. Nous vous repondrons dans les plus brefs delais.';
        this.contactData = { nom: '', email: '', sujet: '', message: '' };
        this.contactSending = false;
      },
      error: () => {
        this.contactError = 'Une erreur est survenue lors de l\'envoi. Veuillez reessayer.';
        this.contactSending = false;
      }
    });
  }

  ngOnDestroy(): void {
    // cleanup if needed
  }

  /** Language switcher */
  switchLang(event: Event) {
    const lang = (event.target as HTMLSelectElement).value;
    this.translate.use(lang);
    localStorage.setItem('selectedLanguage', lang);
  }

  /** Mobile menu toggle */
  toggleMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    const menu = document.getElementById('bk-mobile-menu');
    if (menu) {
      menu.classList.toggle('open', this.mobileMenuOpen);
    }
  }

  scrollToFeatures() {
    const el = document.getElementById('features');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }

  /** Navbar scroll effect */
  private initNavbarScroll() {
    const nav = document.getElementById('bk-navbar');
    const onScroll = () => {
      if (nav) {
        nav.classList.toggle('scrolled', window.scrollY > 60);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /** Establishment carousel */
  private initCarousel() {
    setTimeout(() => {
      const track = document.getElementById('bk-estab-track');
      const prevBtn = document.getElementById('carousel-prev');
      const nextBtn = document.getElementById('carousel-next');

      if (!track) return;

      const cards = track.querySelectorAll('.bk-estab-card');
      const cardWidth = 220; // card width + gap
      this.carouselMax = Math.max(0, cards.length - 3);

      const update = () => {
        track.style.transform = `translateX(-${this.carouselIndex * cardWidth}px)`;
      };

      prevBtn?.addEventListener('click', () => {
        if (this.carouselIndex > 0) { this.carouselIndex--; update(); }
      });

      nextBtn?.addEventListener('click', () => {
        if (this.carouselIndex < this.carouselMax) { this.carouselIndex++; update(); }
      });
    }, 300);
  }

  /** ✅ Recalculer le max du carousel après chargement des établissements */
  private updateCarouselMax() {
    const track = document.getElementById('bk-estab-track');
    if (!track) return;

    const cards = track.querySelectorAll('.bk-estab-card');
    this.carouselMax = Math.max(0, cards.length - 3);
  }

  /** Close mobile menu on outside click */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const burger = document.getElementById('nav-burger');
    const menu   = document.getElementById('bk-mobile-menu');
    const target = event.target as Node;

    if (this.mobileMenuOpen && menu && burger &&
        !menu.contains(target) && !burger.contains(target)) {
      this.mobileMenuOpen = false;
      menu.classList.remove('open');
    }
  }
}
